<template>
    <AdminPageInner v-if="sessionGroup.id" :breadcrumbs="breadcrumbs">
        <template #heading>
            <CalendarFormat :date="startDate" :showState="false"/>
            -
            <CalendarFormat :date="endDate" :showState="false"/>
        </template>
        <template v-if="sessionGroup.id" #links>
            <ul class="flex items-center space-x-4">
                <li>
                    <router-link
                        :to="{name: 'admin.courses.session-groups.applications.index', params: $route.params}">
                        Applications
                    </router-link>
                </li>
                <li v-if="sessionGroup.user_can.update">
                    <router-link
                        :to="{name: 'admin.courses.session-groups.edit', params: {courseId: course.id, sessionGroupGroupId: sessionGroup.id}}">
                        Edit
                    </router-link>
                </li>
                <li v-if="sessionGroup.user_can.postpone && !sessionGroup.postponed_at">
                    <button
                        class="text-yellow-700"
                        @click.prevent="confirmPostpone">
                        Postpone
                    </button>
                </li>
                <li v-if="sessionGroup.user_can.resume && sessionGroup.postponed_at">
                    <button
                        class="text-green-600"
                        @click.prevent="confirmResume">
                        Resume
                    </button>
                </li>
                <li v-if="sessionGroup.user_can.delete">
                    <button
                        class="text-red-600"
                        @click.prevent="confirmDelete">
                        Delete
                    </button>
                </li>
            </ul>
        </template>

        <div v-if="sessionGroup.postponed_at && hasUnrecordedSessions" class="bg-yellow-400 mb-4 rounded-md">
            <div class="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8">
                <div class="flex items-center flex-wrap">
                    <div class="flex items-center">
                        <div class="flex p-2 rounded-lg bg-yellow-600">
                            <!-- Heroicon name: outline/speakerphone -->
                            <ExclamationIcon class="h-4 w-4 text-white"/>
                        </div>
                    </div>
                    <p class="ml-5 font-medium text-yellow-800 ">
                        There are session(s) for this group that are set in the past but don't have any attendance
                        records.
                        <br>
                        Please consider adding records or changing the date of the session(s) before
                        <strong>resuming</strong> the group.
                    </p>
                </div>
            </div>
        </div>

        <div class="row gutters-3 lg:flex-row-reverse">
            <div class="col-full lg:col-auto lg:min-w-96">
                <InfoCard>
                    <InfoCardItem title="Spaces">
                        <div class="text-right">{{ availableSpaces }} / {{ sessionGroup.spaces }}</div>
                    </InfoCardItem>
                    <InfoCardItem title="Accepted">
                        <div class="text-right">{{ sessionGroup.accepted_applications_count }}</div>
                    </InfoCardItem>
                    <InfoCardItem title="Rejected">
                        <div class="text-right">{{ sessionGroup.rejected_applications_count }}</div>
                    </InfoCardItem>
                    <InfoCardItem title="Pending">
                        <div class="text-right">{{ sessionGroup.pending_applications_count }}</div>
                    </InfoCardItem>
                </InfoCard>
            </div>
            <div class="col-full mt-6 space-y-6 lg:col lg:mt-0">

                <div class="space-y-6">
                    <Card v-for="session in sessions">
                        <template #header>
                            <div class="row">
                                <div class="col">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                                        {{ session.module.title }}
                                    </h3>
                                </div>
                                <div class="col-auto">
                                    <router-link
                                        v-if="session.user_can.record && sessionGroup.accepted_applications_count"
                                        :to="{
                                            name: 'admin.courses.session-groups.sessions.records.index',
                                            params: {
                                                courseId: $route.params.courseId,
                                                courseSessionGroupId: $route.params.courseSessionGroupId,
                                                courseSessionId: session.id
                                            }
                                        }">
                                        Records
                                    </router-link>
                                </div>
                            </div>
                        </template>
                        <dl class="space-y-6">
                            <div class="row">
                                <div class="col-full sm:col-1/2">
                                    <dt class="leading-5 font-medium text-gray-500">
                                        Start
                                    </dt>
                                    <CalendarFormat :date="session.start" :showState="false"/>
                                </div>
                                <div class="col-full sm:col-1/2">
                                    <dt class="leading-5 font-medium text-gray-500">
                                        End
                                    </dt>
                                    <CalendarFormat :date="session.end" :showState="false"/>
                                </div>
                            </div>
                            <div v-if="session.type === 'Zoom'">
                                <dt class="leading-5 font-medium text-gray-500">
                                    Location Link
                                </dt>
                                <dd class="mt-4">
                                    <a :href="session.location_url" target="_blank" rel="noreferrer">
                                        {{ session.location_url }}
                                    </a>
                                </dd>
                            </div>
                            <div>
                                <dt class="leading-5 font-medium text-gray-500">
                                    {{ session.type === 'Zoom' ? 'Location Notes' : 'Location Details' }}
                                </dt>
                                <dd class="mt-4 whitespace-pre-line" v-text="session.location_notes"/>
                            </div>
                        </dl>
                    </Card>
                </div>
            </div>
        </div>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import {CourseApplicationStatus} from "@/Lib/Enums";
import AdminPageInner from "@/Components/Layout/AdminPageInner";
import {ExclamationIcon} from "@heroicons/vue/outline";
import {InfoCard, InfoCardItem} from "@/Components/InfoCard";
import CalendarFormat from "@/Components/CalendarFormat";
import Card from "@/Components/Card";
import dayjs from "dayjs";
import {sortBy} from "lodash";

export default {

    name: "CourseSessionGroupsShowPage",

    data() {
        return {
            CourseApplicationStatus,
            sessionGroup: {},
            course: {},
        }
    },

    components: {
        AdminPageInner,
        ExclamationIcon,
        InfoCard,
        InfoCardItem,
        CalendarFormat,
        Card
    },

    methods: {
        setData(sessionGroup) {
            this.sessionGroup = sessionGroup;
            this.course = sessionGroup.course;
        },
        confirmDelete() {
            if (window.confirm('Are you sure you want to delete this Course session group?')) {
                axios.delete(route('api.course-session-groups.destroy', this.sessionGroup))
                    .then(() => {
                        this.$notify.success('Course session group deleted');
                        this.$router.push({
                            name: 'admin.courses.session-groups.index',
                            params: {courseId: this.sessionGroup.course_id}
                        });
                    })
                    .catch(console.error);
            }
        },
        confirmPostpone() {
            if (window.confirm('Are you sure you want to postpone this Course session group?')) {
                axios.patch(route('api.course-session-groups.postpone', this.sessionGroup))
                    .then(() => {
                        this.$notify.success('Course Session Group postponed');
                        this.$router.push({
                            name: this.$route.name,
                            params: this.$route.params,
                            query: _.assign({}, this.$route.query, {t: dayjs().unix()})
                        });
                    })
                    .catch(console.error);
            }
        },
        confirmResume() {
            if (window.confirm('Are you sure you want to resume this Course session group?')) {
                axios.patch(route('api.course-session-groups.resume', this.sessionGroup))
                    .then(() => {
                        this.$notify.success('Course Session Group resumed');
                        this.$router.push({
                            name: this.$route.name,
                            params: this.$route.params,
                            query: _.assign({}, this.$route.query, {t: dayjs().unix()})
                        });
                    })
                    .catch(console.error);
            }
        },
    },

    computed: {
        breadcrumbs() {
            return [
                {
                    label: 'Admin', to: {name: 'admin'}
                },
                {
                    label: 'Courses', to: {name: 'admin.courses.index'}
                },
                {
                    label: `${this.course.title || 'Course'}`,
                    to: {name: 'admin.courses.show', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Session Groups`,
                    to: {name: 'admin.courses.session-groups.index', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Group`, to: {name: this.$route.name, params: this.$route.params}
                },
            ];
        },
        startDate() {
            const sessions = sortBy(this.sessionGroup.sessions, session => dayjs(session.start));

            return sessions[0].start;
        },
        endDate() {
            const sessions = sortBy(this.sessionGroup.sessions, session => dayjs(session.end)).reverse();

            return sessions[0].end;
        },
        sessions() {
            return sortBy(this.sessionGroup.sessions, session => dayjs(session.start))
        },
        availableSpaces() {
            return this.sessionGroup.spaces - this.sessionGroup.accepted_applications_count;
        },
        totalApplications() {
            const {
                accepted_applications_count,
                rejected_applications_count,
                pending_applications_count
            } = this.sessionGroup;
            return accepted_applications_count + rejected_applications_count + pending_applications_count
        },
        hasUnrecordedSessions() {
            return this.sessionGroup.sessions.filter(item => {
                return dayjs(item.start).isBefore(dayjs()) && item.records_count < 1;
            }).length > 0;
        }
    },

    async beforeRouteEnter(to, from, next) {
        const data = (await axios.get(route('api.course-session-groups.show', to.params.courseSessionGroupId), {params: to.query})).data;
        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {
        const data = (await axios.get(route('api.course-session-groups.show', to.params.courseSessionGroupId), {params: to.query})).data;
        this.setData(data);
    },
};
</script>
